import type { AwsRegionId } from "../lib/awsRegion";
import { AWS_REGION_IDS } from "../lib/awsRegion";
import type { Locale } from "../i18n/locale";
import type { UiKey } from "../i18n/ui";
import { uiString } from "../i18n/ui";
import type { DisplayCurrency } from "../lib/displayCurrency";

type Props = {
  locale: Locale;
  t: (key: UiKey) => string;
  region: AwsRegionId;
  onRegion: (r: AwsRegionId) => void;
  budgetStr: string;
  onBudgetStr: (s: string) => void;
  displayCurrency: DisplayCurrency;
  onDisplayCurrency: (c: DisplayCurrency) => void;
};

export function EstimatorToolbar({
  locale,
  t,
  region,
  onRegion,
  budgetStr,
  onBudgetStr,
  displayCurrency,
  onDisplayCurrency,
}: Props) {
  return (
    <div className="toolbar">
      <div className="field">
        <label htmlFor="aws-region" className="lab">
          {t("toolbar.region")}
        </label>
        <select
          id="aws-region"
          value={region}
          onChange={(e) => onRegion(e.target.value as AwsRegionId)}
        >
          {AWS_REGION_IDS.map((id) => (
            <option key={id} value={id}>
              {uiString(locale, `region.${id}` as UiKey)}
            </option>
          ))}
        </select>
        <p className="hint">{t("toolbar.regionHelp")}</p>
      </div>
      <div className="field">
        <label htmlFor="budget-cap" className="lab">
          {t("toolbar.budgetCap")}
        </label>
        <input
          id="budget-cap"
          type="text"
          inputMode="decimal"
          placeholder={t("toolbar.budgetPlaceholder")}
          value={budgetStr}
          onChange={(e) => onBudgetStr(e.target.value)}
        />
      </div>
      <div className="field field--currency">
        <span className="lab" id="display-currency-label">
          {t("toolbar.displayCurrency")}
        </span>
        <div
          className="currency-tabs"
          role="tablist"
          aria-labelledby="display-currency-label"
        >
          {(
            [
              ["USD", "toolbar.currencyUsd"] as const,
              ["EUR", "toolbar.currencyEur"] as const,
            ] as const
          ).map(([code, labelKey]) => (
            <button
              key={code}
              type="button"
              role="tab"
              aria-selected={displayCurrency === code}
              className={displayCurrency === code ? "c-tab active" : "c-tab"}
              onClick={() => onDisplayCurrency(code)}
            >
              {t(labelKey)}
            </button>
          ))}
        </div>
        {displayCurrency === "EUR" ? (
          <p className="hint hint--eur">{t("toolbar.currencyEurHint")}</p>
        ) : null}
      </div>

      <style>{`
        .toolbar {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0 1rem;
          align-items: start;
        }
        .field--currency {
          grid-column: 1 / -1;
        }
        .currency-tabs {
          display: flex;
          flex-wrap: wrap;
          gap: 0.4rem;
        }
        .c-tab {
          border: 1px solid var(--border);
          background: var(--surface2);
          color: var(--muted);
          padding: 0.35rem 0.65rem;
          border-radius: 8px;
          cursor: pointer;
          font: inherit;
          font-size: 0.8rem;
          font-weight: 600;
        }
        .c-tab.active {
          border-color: var(--accent-dim);
          color: var(--text);
          background: var(--accent-chip);
        }
        .c-tab:focus-visible {
          outline: 2px solid var(--accent);
          outline-offset: 2px;
        }
        .hint--eur {
          margin-top: 0.35rem;
        }
        @media (max-width: 640px) {
          .toolbar {
            grid-template-columns: 1fr;
            gap: 1rem 0;
          }
        }
        .field {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
          min-width: 0;
        }
        .lab {
          margin: 0;
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--muted);
          line-height: 1.3;
          min-height: 2.6em;
          display: flex;
          align-items: flex-end;
        }
        .field select,
        .field input {
          width: 100%;
          max-width: 22rem;
          min-height: 2.375rem;
          box-sizing: border-box;
          padding: 0.45rem 0.65rem;
          border-radius: 8px;
          border: 1px solid var(--border);
          background: var(--surface2);
          color: var(--text);
          font-family: var(--font);
          font-size: 0.9rem;
        }
        .hint {
          margin: 0;
          padding-top: 0.1rem;
          font-size: 0.75rem;
          line-height: 1.45;
          color: var(--muted);
          max-width: 28rem;
        }
        @media (max-width: 640px) {
          .field select,
          .field input {
            max-width: none;
            min-height: 48px;
            font-size: 1rem;
            padding: 0.55rem 0.75rem;
          }
        }
      `}</style>
    </div>
  );
}
